import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import CyberCubLogo from '../../components/CyberCubLogo.jsx';
import Tutor from '../../components/Tutor.jsx';
import MacDesktopArt from './MacDesktop.jsx';
import { useFinished } from '../../lib/practical.js';

/**
 * Practical site for "macOS Basics".
 *
 * The same three sections as the Windows and Linux practicals, for the
 * same reason: the third operating system should take an hour, and it
 * will if the shape of the lesson does not change underneath the
 * learner.
 *
 * The third section has one more option than the others do, and that is
 * the point of it. Windows and Linux both answer "who is allowed?" with
 * two answers. A Mac has a third - nobody, not even root - because
 * System Integrity Protection puts part of the disk beyond everybody,
 * and that is a genuinely different idea worth meeting.
 */

const STORE_KEY = 'cybercub.lab.macos-basics';

/* ------------------------------------------------------------------ */
/* 1. find your way around the desktop                                */
/* ------------------------------------------------------------------ */

const SPOTS = [
  {
    id: 'apple',
    name: 'Apple menu',
    x: 2.2, y: 0.3, w: 4.2, h: 4.7,
    ask: 'Where do you click to reach About This Mac, Sleep, Restart and Shut Down?',
    why: 'The Apple menu. It is in the same place in every program, which is the whole idea of it.',
  },
  {
    id: 'menubar',
    name: 'The menu bar',
    x: 7, y: 0.3, w: 31, h: 4.7,
    ask: 'Which strip changes its contents depending on which program you are using?',
    why: 'The menu bar. On a Mac the menus live at the top of the screen and belong to the program, not to the window — which is why the strip changes as you click between apps.',
  },
  {
    id: 'finder',
    name: 'Finder',
    x: 26.6, y: 87.6, w: 6, h: 9.5,
    ask: 'Which icon opens the files and folders on this machine?',
    why: 'Finder, at the left end of the Dock — the same job as File Explorer. It is always running, and it is always the leftmost icon.',
  },
  {
    id: 'dock',
    name: 'The Dock',
    x: 54.4, y: 88, w: 6.9, h: 10,
    ask: 'Which strip along the bottom holds the programs you use most?',
    why: 'The Dock. Pinned programs sit at the left, the Trash at the right, and a small dot under an icon means that program is open.',
  },
  {
    id: 'spotlight',
    name: 'Spotlight',
    x: 73.5, y: 0.3, w: 4.5, h: 4.7,
    ask: 'Where do you click to search the whole Mac — files, settings and programs at once?',
    why: 'Spotlight, the magnifying glass. Command and Space opens the same thing from anywhere, and it is how most Mac users start every program they own.',
  },
];

/* ------------------------------------------------------------------ */
/* 2. read a path                                                     */
/* ------------------------------------------------------------------ */

const PATHS = [
  {
    id: 'p1',
    path: '/Users/aarav/Documents/homework.pages',
    q: 'Whose folder is this file in?',
    options: ['Nobody — it is a system file', "Aarav's", 'Everyone on the machine', 'The root account'],
    answer: 1,
    why: 'macOS is a Unix underneath, so it looks like the Linux path with one word changed: /Users rather than /home. No drive letters here either.',
  },
  {
    id: 'p2',
    path: '/Applications/Safari.app',
    q: 'A .app looks like a single file in Finder. What is it really?',
    options: [
      'A shortcut to a program',
      'A folder full of files, which Finder draws as one thing',
      'A compressed archive',
      'A setting',
    ],
    answer: 1,
    why: 'It is a bundle — a folder holding the program, its pictures and its translations. Right-click and Show Package Contents opens it like any other folder.',
  },
  {
    id: 'p3',
    path: '/System/Library/',
    q: 'Why can an administrator not change anything in here?',
    options: [
      'It is empty',
      'System Integrity Protection puts it beyond everybody, including root',
      'It belongs to another user',
      'It is on a different disk',
    ],
    answer: 1,
    why: 'macOS keeps the system on a separate, sealed, read-only volume. Not being able to change it is the protection — and it is why a Mac has a third answer to "who is allowed?" that Windows and Linux do not.',
  },
  {
    id: 'p4',
    path: '/Volumes/BACKUP/',
    q: 'What is /Volumes for?',
    options: [
      'Sound settings',
      'Where other disks get attached — a USB stick, an external drive, a mounted image',
      'Deleted files',
      'Installed programs',
    ],
    answer: 1,
    why: 'Plug in a USB stick and it appears as a folder under /Volumes. Same idea as Linux, and the same absence of drive letters.',
  },
  {
    id: 'p5',
    path: '/Users/aarav/Downloads/installer.dmg',
    q: 'What is a .dmg?',
    options: [
      'A program that runs straight away',
      'A disk image — a file pretending to be a disk, which appears under /Volumes when opened',
      'A picture',
      'A document',
    ],
    answer: 1,
    why: 'Opening one mounts it like a disk; you then drag the app out into /Applications. Because it behaves like a disk, it is also a tidy way to hand somebody a program they did not ask for.',
  },
  {
    id: 'p6',
    path: '/Users/aarav/Library/',
    q: 'Finder does not show this folder. Why not?',
    options: [
      'It does not exist',
      'Apple hides it — it holds your programs’ settings, and changing it by accident breaks things',
      'Only root can see it',
      'It is a shortcut',
    ],
    answer: 1,
    why: 'Hidden, not protected. Command-Shift-Full stop shows hidden files in Finder, and the terminal never hid it in the first place — which is the same lesson the other two operating systems taught.',
  },
];

/* ------------------------------------------------------------------ */
/* 3. who is allowed?                                                 */
/* ------------------------------------------------------------------ */

/*
 * Three answers rather than two. The third one is the reason this
 * section exists: on a Mac there is a part of the disk that nobody at
 * all may write to, and a learner who has met only Windows will not
 * expect that to be possible.
 */
const RIGHT_OPTIONS = [
  { label: 'Needs an administrator password', icon: '🛡️' },
  { label: 'A normal user can do this', icon: '👤' },
  { label: 'Nobody — macOS protects it from everybody', icon: '🔒' },
];

const RIGHTS = [
  {
    id: 'r1',
    icon: '🖼️',
    text: 'Aarav wants to change the desktop wallpaper on his own account.',
    answer: 1,
    why: 'It changes only his own settings. Nothing outside his home folder moves.',
  },
  {
    id: 'r2',
    icon: '📦',
    text: 'A program wants to install itself into /Applications, where everybody can use it.',
    answer: 0,
    why: 'Anything for every account changes the machine, and macOS asks for an administrator password before it will.',
  },
  {
    id: 'r3',
    icon: '🗄️',
    text: 'Something wants to write a file into /System/Library.',
    answer: 2,
    why: 'Nobody can. The system sits on a sealed, read-only volume, and not even root may change it. On Windows or Linux this same act would simply need administrator rights.',
  },
  {
    id: 'r4',
    icon: '📄',
    text: 'Meera saves an essay into /Users/meera/Documents.',
    answer: 1,
    why: 'Her folder, her file. This is what a normal account is for, and it is most of what anybody does all day.',
  },
];

/* ------------------------------------------------------------------ */

const load = () => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) ?? '{}'); } catch { return {}; }
};
const save = (state) => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(state)); } catch { /* private window */ }
};

export default function MacBasicsLab() {
  const saved = useMemo(load, []);

  const [tab, setTab] = useState('desktop');
  const [found, setFound] = useState(saved.found ?? []);
  const [wrong, setWrong] = useState(null);
  const [pathPicks, setPathPicks] = useState(saved.pathPicks ?? {});
  const [rightPicks, setRightPicks] = useState(saved.rightPicks ?? {});

  useEffect(() => {
    save({ found, pathPicks, rightPicks });
  }, [found, pathPicks, rightPicks]);

  useEffect(() => { document.title = 'macOS Basics · CyberCub Lab'; }, []);

  const asking = SPOTS.find((s) => !found.includes(s.id)) ?? null;
  const desktopDone = found.length === SPOTS.length;
  const pathsDone = PATHS.every((p) => pathPicks[p.id] === p.answer);
  const rightsDone = RIGHTS.every((r) => rightPicks[r.id] === r.answer);

  useFinished('/lab/macos-basics', rightsDone);

  const TABS = [
    { id: 'desktop', n: 1, label: 'Find your way around', locked: false },
    { id: 'paths', n: 2, label: 'Read a path', locked: !desktopDone },
    { id: 'rights', n: 3, label: 'Who is allowed?', locked: !pathsDone },
  ];

  const pick = (id) => {
    if (!asking) return;
    if (id === asking.id) {
      setWrong(null);
      setFound((f) => [...f, id]);
      return;
    }
    setWrong(id);
    setTimeout(() => setWrong(null), 700);
  };

  const back = (() => {
    if (tab === 'desktop') return null;
    if (tab === 'paths') return { label: '← Back to the desktop', act: () => setTab('desktop') };
    return { label: '← Back to the paths', act: () => setTab('paths') };
  })();

  return (
    <div className="lab">
      <header className="lab-top">
        <div className="row gap-sm">
          <CyberCubLogo size={38} />
          <div>
            <strong>macOS Basics</strong>
            <span className="lab-sub">Practical · Ethical Hacker, Module 2</span>
          </div>
        </div>
        <Link to="/learn/lesson/cf5-macos-basics" className="btn btn-sm btn-ghost">
          ← Back to the lesson
        </Link>
      </header>

      <nav className="lab-tabs" aria-label="Sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`lab-tab ${tab === t.id ? 'is-active' : ''} ${t.locked ? 'is-locked' : ''}`}
            onClick={() => !t.locked && setTab(t.id)}
            disabled={t.locked}
            aria-current={tab === t.id ? 'page' : undefined}
          >
            <span className="lab-tab-n">{t.n}</span>
            {t.label}
            {t.locked && <span aria-label="locked"> 🔒</span>}
          </button>
        ))}
      </nav>

      {/* ---------------- 1. the desktop ---------------- */}
      {tab === 'desktop' && (
        <section className="lab-section">
          <div className="place-head">
            <div>
              <h2>Find your way around</h2>
              <p className="lab-lead">
                A Mac desktop with nothing labelled. Read what is being asked for, then click it on
                the picture.
              </p>
            </div>
            <span className="pill">{found.length} / {SPOTS.length} found</span>
          </div>

          <Tutor
            avatar="cub-bubble"
            say={
              desktopDone
                ? 'All five. The one that catches every Windows user out is the menu bar: it is at the top of the screen, not the top of the window, and it belongs to whichever program you are in. Which also means the red button closes the window and leaves the program running.'
                : asking
                  ? `Find this one: ${asking.ask}`
                  : 'Click the part of the desktop being asked for.'
            }
          />

          {asking && (
            <div className="win-ask">
              <span className="win-ask-n" aria-hidden="true">{found.length + 1}</span>
              <p>{asking.ask}</p>
            </div>
          )}

          <div className="win-desktop">
            <MacDesktopArt showFinder />
            {SPOTS.map((s) => {
              const done = found.includes(s.id);
              const isWrong = wrong === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  className={`win-spot ${done ? 'is-found' : ''} ${isWrong ? 'is-wrong' : ''}`}
                  style={{ left: `${s.x}%`, top: `${s.y}%`, width: `${s.w}%`, height: `${s.h}%` }}
                  onClick={() => pick(s.id)}
                  aria-label={done ? `${s.name}, found` : 'Unlabelled part of the desktop'}
                  disabled={done || !asking}
                >
                  {done && <span className="win-spot-tag">{s.name}</span>}
                </button>
              );
            })}
          </div>

          {found.length > 0 && (
            <ul className="win-learned">
              {SPOTS.filter((s) => found.includes(s.id)).map((s) => (
                <li key={s.id}><strong>{s.name}.</strong> {s.why}</li>
              ))}
            </ul>
          )}

          {desktopDone && (
            <div className="lab-done">
              <strong>All five.</strong> Now find out where the files behind those icons actually live.
              <button type="button" className="btn btn-sm" onClick={() => setTab('paths')}>
                Read a path →
              </button>
            </div>
          )}
        </section>
      )}

      {/* ---------------- 2. read a path ---------------- */}
      {tab === 'paths' && (
        <section className="lab-section">
          <div className="place-head">
            <div>
              <h2>Read a path</h2>
              <p className="lab-lead">
                A Mac is a Unix underneath, so these look a lot like the Linux ones — one tree
                starting at <code>/</code>, and no drive letters. Six real paths: work out what each
                is telling you.
              </p>
            </div>
            <div className="row gap-sm">
              <button type="button" className="btn btn-sm btn-ghost" onClick={back.act}>
                {back.label}
              </button>
              <span className="pill">
                {PATHS.filter((p) => pathPicks[p.id] === p.answer).length} / {PATHS.length}
              </span>
            </div>
          </div>

          <Tutor
            avatar="cub-grape"
            say={
              pathsDone
                ? 'All six. Two to carry away: a .app is a folder wearing a costume, and /System is sealed against everybody. The first explains why a Mac program can be dragged to the bin like a file. The second is why a Mac has an answer to "who is allowed?" that the other two do not.'
                : 'Read each path from the first slash. If you have done the Linux lesson most of this will feel familiar — /Users is where /home was, and /Volumes is where a USB stick turns up.'
            }
          />

          {PATHS.map((p) => {
            const pick2 = pathPicks[p.id];
            const right = pick2 === p.answer;
            return (
              <div key={p.id} className={`lab-case ${right ? 'is-right' : pick2 !== undefined ? 'is-wrong' : ''}`}>
                <div className="grow">
                  <code className="win-path">{p.path}</code>
                  <p><strong>{p.q}</strong></p>
                  <div className="lab-choices">
                    {p.options.map((opt, oi) => (
                      <button
                        key={opt}
                        type="button"
                        className={`lab-choice ${pick2 === oi ? 'is-picked' : ''}`}
                        onClick={() => setPathPicks((prev) => ({ ...prev, [p.id]: oi }))}
                        disabled={right}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                  {pick2 !== undefined && (
                    <p className={`lab-feedback ${right ? 'is-right' : 'is-wrong'}`}>
                      {right ? `✓ ${p.why}` : '✗ Not that one — read the path again from the first slash.'}
                    </p>
                  )}
                </div>
              </div>
            );
          })}

          {pathsDone && (
            <div className="lab-done">
              <strong>All six read.</strong> One question left, and this time there are three possible
              answers rather than two.
              <button type="button" className="btn btn-sm" onClick={() => setTab('rights')}>
                Who is allowed? →
              </button>
            </div>
          )}
        </section>
      )}

      {/* ---------------- 3. who is allowed ---------------- */}
      {tab === 'rights' && (
        <section className="lab-section">
          <div className="place-head">
            <div>
              <h2>Who is allowed?</h2>
              <p className="lab-lead">
                Three answers this time. An administrator can change most of the machine, a normal
                user can change their own things — and part of a Mac is sealed against everybody,
                including root.
              </p>
            </div>
            <div className="row gap-sm">
              <button type="button" className="btn btn-sm btn-ghost" onClick={back.act}>
                {back.label}
              </button>
              <span className="pill">
                {RIGHTS.filter((r) => rightPicks[r.id] === r.answer).length} / {RIGHTS.length}
              </span>
            </div>
          </div>

          <Tutor
            avatar="cub-coral"
            say={
              rightsDone
                ? 'That third answer is the Mac’s own idea. Windows and Linux both say "an administrator may change anything"; macOS says there is a part of itself that nobody may change, and takes the decision away from the person at the keyboard entirely. It is also why so much Mac malware tries to talk the user into turning protections off rather than breaking them.'
                : 'Ask two questions each time. Does this change only my own things, or the machine for everybody? And is this part of the machine one that even root is locked out of?'
            }
          />

          {RIGHTS.map((r) => {
            const pick3 = rightPicks[r.id];
            const right = pick3 === r.answer;
            return (
              <div key={r.id} className={`lab-case ${right ? 'is-right' : pick3 !== undefined ? 'is-wrong' : ''}`}>
                <span className="lab-case-n" aria-hidden="true">{r.icon}</span>
                <div className="grow">
                  <p>{r.text}</p>
                  <div className="lab-choices">
                    {RIGHT_OPTIONS.map((opt, oi) => (
                      <button
                        key={opt.label}
                        type="button"
                        className={`lab-choice ${pick3 === oi ? 'is-picked' : ''}`}
                        onClick={() => setRightPicks((prev) => ({ ...prev, [r.id]: oi }))}
                        disabled={right}
                      >
                        {opt.icon} {opt.label}
                      </button>
                    ))}
                  </div>
                  {pick3 !== undefined && (
                    <p className={`lab-feedback ${right ? 'is-right' : 'is-wrong'}`}>
                      {right ? `✓ ${r.why}` : '✗ Not quite — whose things does it change, and is this a part even root cannot touch?'}
                    </p>
                  )}
                </div>
              </div>
            );
          })}

          {rightsDone && (
            <div className="lab-done">
              <div>
                <strong>All four.</strong>
                <p style={{ margin: '0.3rem 0 0' }}>
                  Three operating systems, one question. Anything that runs as you can do exactly
                  what you can do — and the only real difference between them is how much of the
                  machine they keep out of reach by default.
                </p>
              </div>
              <Link to="/learn/lesson/cf5-macos-basics" className="btn btn-sm">
                ← Back to the lesson
              </Link>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
